import { useContext, useEffect, useState, useRef } from 'react';
import api from '../api';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Store } from '../Store';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import { PASSWORD_PATTERN } from '../utils/passwordRule';
import Popover from 'react-bootstrap/Popover';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';

export default function SignupScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate(); // enables the functionality, to navigate a user to a different screen
  const { search } = useLocation(); // accesses the query string portion of the URL, including the leading ? character and any key-value pairs separated by & characters
  const redirectInUrl = new URLSearchParams(search).get('redirect'); // gets the value to the 'redirect' key from the query string portion of the URL (in this case '/shipping-address')
  const redirect = redirectInUrl ? redirectInUrl : '/'; // if redirectInUrl exists, redirect will be set to 'shipping-address', otherwise to '/'

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [receiveMarketingEmails, setReceiveMarketingEmails] = useState(true);
  const recaptchaRef = useRef(null);

  const { state } = useContext(Store); // useContext gives access to the state of the context (in this case the Store)
  const { userInfo } = state;

  useEffect(() => {
    if (userInfo) {
      navigate(redirect);
    }
  }, [navigate, redirect, userInfo]); // when the user is already signed-in, he can't enter the SignupScreen and instead will be redirected to the redirect variable (the dependency array hold all variable that are being used)

  const submitHandler = async (e) => {
    e.preventDefault(); // prevents a page refresh, whenever the signin button is clicked

    if (!agreedToTerms) {
      toast.error(t('signup-screen-error-0-1'));
      return;
    } else if (!recaptchaToken) {
      toast.error(t('signup-screen-error-0-2'));
      return;
    }

    const { data } = await api.post('/api/general/verify-recaptcha', {
      token: recaptchaToken,
    }); // sending the recaptchaToken to the backend for verification

    if (data.success) {
      if (password !== confirmPassword) {
        toast.error(t('signup-screen-error-1'));
        return;
      } // checks if the password and confirmedPassword match
      try {
        const baseUrl = window.location.origin;
        const currentLanguage = i18n.language; // Gets the current language
        await api.post('/api/users/signup', {
          username,
          email,
          password,
          baseUrl,
          receiveMarketingEmails,
          redirect,
          language: currentLanguage,
        }); // axios post request to '/api/users/signup', that contains the username, email and password in the body of the post request
        toast.success(t('signup-screen-success'));
        navigate(`/please-verify-your-email`);
      } catch (err) {
        const errorMessage = err.response?.data?.message || '';
        if (errorMessage.includes('E-Mail')) {
          setEmailError(err.response.data.message);
        } else {
          toast.error(t(getError(err))); // using the error message as the key for the translation json files. If the key doesn't exist, the message itself will be displayed inside the toest.error
        }
      }
    } else {
      toast.error(t('signup-screen-error-2'));
      setRecaptchaToken('');
      recaptchaRef.current.reset(); // Reset the reCAPTCHA widget
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  return (
    <Container>
      <div className="container small-container">
        <title>{t('signup-screen-helmet')}</title>

        <h1 className="screen-heading">{t('signup-screen-heading')}</h1>

        <Form onSubmit={submitHandler} className="signup-form">
          <Form.Group controlId="username">
            <Form.Label>{t('signup-screen-username')}</Form.Label>
            <Form.Control
              className="mobile-device-compatible"
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group controlId="email">
            <Form.Label>{t('signup-screen-email')}</Form.Label>
            <Form.Control
              className="mobile-device-compatible"
              type="email"
              required
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(''); // Clears the email error when the user starts typing again
              }}
              isInvalid={emailError.length > 0} // Marks the control as invalid if there's an email error
            />
            {emailError && (
              <Form.Control.Feedback className="feedback" type="invalid">
                {emailError}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group controlId="password">
            <Form.Label>
              <div className="display-inline">
                {t('signup-screen-password')}

                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="bottom"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Header as="h3">
                        {t('signup-screen-popover-heading')}
                      </Popover.Header>
                      <Popover.Body>
                        <ul>
                          <li>{t('signup-screen-popover-li-1')}</li>
                          <li>{t('signup-screen-popover-li-2')}</li>
                          <li>{t('signup-screen-popover-li-3')}</li>
                          <li>{t('signup-screen-popover-li-4')}</li>
                          <li>{t('signup-screen-popover-li-5')}</li>
                        </ul>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </div>
            </Form.Label>

            <Form.Control
              className="mobile-device-compatible"
              type={passwordVisible ? 'text' : 'password'}
              pattern={PASSWORD_PATTERN}
              required
              onChange={(e) => setPassword(e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="confirmPassword">
            <Form.Label>{t('signup-screen-confirm-password')}</Form.Label>

            <Form.Control
              className="mobile-device-compatible"
              type={passwordVisible ? 'text' : 'password'}
              pattern={PASSWORD_PATTERN}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="show-password" controlId="passwordVisibility">
            <Form.Check
              className="view-password"
              type="checkbox"
              label={t('signup-screen-show-password')}
              onChange={() => setPasswordVisible(!passwordVisible)}
              checked={passwordVisible}
            />
          </Form.Group>

          <div className="finish-signup">
            <Form.Group
              className="marketing-email-container"
              controlId="receiveMarketingEmails"
            >
              <Form.Check
                className="signup-marketing-emails"
                type="checkbox"
                label={t('signup-screen-receive-marketing-emails')}
                checked={receiveMarketingEmails}
                onChange={(e) => setReceiveMarketingEmails(e.target.checked)}
              />
            </Form.Group>

            <Form.Group className="terms-container" controlId="acceptTerms">
              <Form.Check
                className="signup-accept-terms"
                type="checkbox"
                label={
                  <>
                    {t('signup-screen-accept-terms-1')}{' '}
                    <Link
                      className="text-link"
                      to={`/general-terms-and-conditions`}
                    >
                      {t('signup-screen-accept-terms-2')}
                    </Link>
                    ,{' '}
                    <Link className="text-link" to={`/right-of-withdrawal`}>
                      {t('signup-screen-accept-terms-5')}
                    </Link>{' '}
                    {t('signup-screen-accept-terms-3')}{' '}
                    <Link className="text-link" to={`/privacy-policy`}>
                      {t('signup-screen-accept-terms-4')}
                    </Link>
                  </>
                }
                onChange={() => setAgreedToTerms(!agreedToTerms)}
                checked={agreedToTerms === true}
              />
            </Form.Group>

            <ReCAPTCHA
              sitekey="6LfzWecpAAAAAIiYVpxC704EIzOF3C0rh0i1Jwq5"
              onChange={handleRecaptchaChange}
              ref={recaptchaRef}
              className="signup-recaptcha"
            />

            <div className="signup-submit">
              <Button variant="primary" type="submit">
                {t('signup-screen-signup')}
              </Button>
            </div>
          </div>

          <div className="signin-link">
            <span className="left-link-dash">{'-'}</span>
            <span className="signin-link-info">
              {t('signup-screen-already-signed-up')}
            </span>
            <span>
              <Link
                className="text-link"
                style={{ whiteSpace: 'nowrap' }}
                to={`/signin?redirect=${redirect}`}
              >
                {t('signup-screen-signin')}
              </Link>
            </span>
            <span className="right-link-dash">{'-'}</span>
          </div>
        </Form>
      </div>
    </Container>
  );
}
